import { z } from 'zod'

const PURCHASE_CANCELLED_ERROR_CODE = '1'
const PRODUCT_ALREADY_PURCHASED_ERROR_CODE = '6'

export type PurchaseResult =
  | { outcome: 'success' }
  | { outcome: 'cancelled' }
  | {
      outcome: 'ownership_mismatch'
      originalAppUserId: string
      currentRevenueCatId: string | null
    }
  | { outcome: 'requires_restore'; errorCode: string }
  | { outcome: 'error'; message: string }

const RESTORE_ERROR_CODES = new Set([
  '7', // RECEIPT_ALREADY_IN_USE_ERROR
  '13', // RECEIPT_IN_USE_BY_OTHER_SUBSCRIBER_ERROR
])

const purchasesErrorSchema = z.object({
  userCancelled: z.boolean().nullish(),
  code: z.string().optional(),
  domain: z.string().optional(),
  message: z.string().optional(),
  userInfo: z
    .object({
      readable_error_code: z.string().optional(),
      underlyingErrorMessage: z.string().optional(),
    })
    .passthrough()
    .optional(),
})

export interface PurchaseErrorDetails {
  code?: string
  domain?: string
  message?: string
  readableErrorCode?: string
  underlyingErrorMessage?: string
  userCancelled: boolean
}

function getPurchaseErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error && error.message) return error.message
  if (
    error != null &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message
  }

  return undefined
}

function getPurchaseErrorCode(error: unknown): string | undefined {
  if (
    error != null &&
    typeof error === 'object' &&
    'code' in error &&
    (typeof error.code === 'string' || typeof error.code === 'number')
  ) {
    return String(error.code)
  }

  return undefined
}

export function getPurchaseErrorDetails(error: unknown): PurchaseErrorDetails {
  const parsed = purchasesErrorSchema.safeParse(error)
  const code = getPurchaseErrorCode(error)
  const message =
    getPurchaseErrorMessage(error) ??
    (parsed.success ? (parsed.data.message ?? undefined) : undefined)

  if (!parsed.success) {
    return {
      code,
      message,
      userCancelled: false,
    }
  }

  return {
    code,
    domain: parsed.data.domain,
    message,
    readableErrorCode: parsed.data.userInfo?.readable_error_code,
    underlyingErrorMessage: parsed.data.userInfo?.underlyingErrorMessage,
    userCancelled: Boolean(parsed.data.userCancelled),
  }
}

export function isAlreadyPurchasedPurchaseMessage(
  message: string | undefined,
): boolean {
  const normalizedMessage = message?.toLowerCase() ?? ''

  return (
    normalizedMessage.includes('alreadypurchasederror') ||
    normalizedMessage.includes('already purchased') ||
    normalizedMessage.includes('already active for the user') ||
    normalizedMessage.includes('already has an active purchase')
  )
}

export function isAlreadyPurchasedPurchaseError(error: unknown): boolean {
  const errorCode = getPurchaseErrorCode(error)
  const message = getPurchaseErrorMessage(error)

  return (
    errorCode === PRODUCT_ALREADY_PURCHASED_ERROR_CODE ||
    isAlreadyPurchasedPurchaseMessage(message)
  )
}

export function resolvePurchaseResult(
  error: unknown,
): Exclude<PurchaseResult, { outcome: 'success' }> {
  const details = getPurchaseErrorDetails(error)
  const errorCode = details.code
  const message = details.message ?? ''

  const isUserCancelled =
    errorCode === PURCHASE_CANCELLED_ERROR_CODE || details.userCancelled

  if (isUserCancelled) {
    return { outcome: 'cancelled' }
  }

  if (errorCode && RESTORE_ERROR_CODES.has(errorCode)) {
    return { outcome: 'requires_restore', errorCode }
  }

  return { outcome: 'error', message }
}
